import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Search from 'pages/Parts/Search'
import Pagination from 'pages/Parts/Pagination'

import { PGET } from 'helpers/api'
import { formatDate, formatMoney, formatDateWithTime } from 'helpers/helpers'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

function Index() {
	const URL = '/services/admin/api/pos-bonus-report-pageList'
	const { t } = useTranslation()

	const today = new Date()
	const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
	const isoDate = (d) => d.toISOString().split('T')[0]

	const [data, setData] = useState([])
	const [pageCount, setPageCount] = useState(0)
	const [totalCount, setTotalCount] = useState(0)
	const [pageSize] = useState(20)
	const [fromDate, setFromDate] = useState(isoDate(firstOfMonth))
	const [toDate, setToDate] = useState(isoDate(today))
	const [searchValue, setSearchValue] = useState('')

	useEffect(() => {
		getData(0)
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	async function getData(page = 0, override = {}) {
		const params = {
			page,
			size: pageSize,
			fromDate: override.fromDate ?? fromDate,
			toDate: override.toDate ?? toDate,
		}
		const s = override.search ?? searchValue
		if (s && s.trim()) params.search = s.trim()

		const response = await PGET(URL, params, { loader: true })
		if (response) {
			setData(response.data || [])
			const total = Number(response.headers['x-total-count'] || 0)
			setTotalCount(total)
			setPageCount(Math.ceil(total / pageSize))
		}
	}

	async function paginate({ selected }) {
		await getData(selected)
	}

	async function onSearch(_, value) {
		setSearchValue(value)
		await getData(0, { search: value })
	}

	function applyDates() {
		getData(0)
	}

	function resetDates() {
		setFromDate(isoDate(firstOfMonth))
		setToDate(isoDate(today))
		getData(0, { fromDate: isoDate(firstOfMonth), toDate: isoDate(today) })
	}

	function totalMonths() {
		return data.reduce((sum, r) => sum + (r.monthsGranted || 0), 0)
	}

	function totalForegone() {
		return data.reduce((sum, r) => sum + (r.foregoneRevenue || 0), 0)
	}

	function downloadExcel() {
		if (data.length === 0) return

		const header = [
			t('ID'),
			t('SALE_POINT_NAME'),
			t('OWNER'),
			t('PERIOD'),
			t('BONUS_MONTHS'),
			t('FOREGONE_REVENUE'),
			t('NOTE'),
			t('GRANTED_BY'),
			t('GRANTED_ON'),
		]

		const body = data.map(item => [
			item.posId,
			item.posName,
			item.ownerLogin,
			`${formatDate(item.paymentFrom)} — ${formatDate(item.paymentTo)}`,
			item.monthsGranted,
			item.foregoneRevenue,
			item.note,
			item.createdBy,
			item.createdDate,
		])

		const ws = XLSX.utils.aoa_to_sheet([header, ...body])
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "Bonus Report")

		const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
		const file = new Blob([buf], { type: "application/octet-stream" })
		saveAs(file, `bonus_report_${fromDate}_${toDate}.xlsx`)
	}

	return (
		<>
			<div className="card">
				<div className="card-body">
					<div className="d-flex mb-3 justify-content-between flex-wrap" style={{ gap: '8px' }}>
						<div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
							<Search url={URL} search={onSearch} getData={getData} />

							<div className="d-flex align-items-center" style={{ gap: '4px' }}>
								<label className="mb-0">{t('FROM_DATE')}</label>
								<input type="date" className="form-control" style={{ width: '160px' }}
									value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
							</div>
							<div className="d-flex align-items-center" style={{ gap: '4px' }}>
								<label className="mb-0">{t('TO_DATE')}</label>
								<input type="date" className="form-control" style={{ width: '160px' }}
									value={toDate} onChange={(e) => setToDate(e.target.value)} />
							</div>
							<button className="btn btn-primary" onClick={applyDates}>{t('APPLY')}</button>
							<button className="btn btn-outline-secondary" onClick={resetDates}>{t('CANCEL')}</button>
						</div>

						<div>
							<button onClick={downloadExcel} className="btn btn-success" disabled={data.length === 0}>
								{t('EXPORT_TO_EXEL')}
							</button>
						</div>
					</div>

					<div className="table-responsive">
						<table className="table mb-0">
							<thead>
								<tr>
									<th>{t('ID')}</th>
									<th>{t('SALE_POINT_NAME')}</th>
									<th>{t('OWNER')}</th>
									<th>{t('PERIOD')}</th>
									<th className="text-center">{t('BONUS_MONTHS')}</th>
									<th className="text-end">{t('FOREGONE_REVENUE')}</th>
									<th>{t('NOTE')}</th>
									<th>{t('GRANTED_BY')}</th>
									<th className="text-center">{t('GRANTED_ON')}</th>
								</tr>
							</thead>
							<tbody>
								{data.map((item, index) => (
									<tr key={index}>
										<td>{item.posId}</td>
										<td>
											<Link to={'/pos-update/' + item.posId}>{item.posName}</Link>
										</td>
										<td>{item.ownerLogin}</td>
										<td className="text-nowrap">
											{formatDate(item.paymentFrom)} — {formatDate(item.paymentTo)}
										</td>
										<td className="text-center">
											<span className="badge bg-success">{item.monthsGranted}</span>
										</td>
										<td className="text-end text-nowrap">{formatMoney(item.foregoneRevenue)}</td>
										<td>{item.note}</td>
										<td>{item.createdBy}</td>
										<td className="text-center text-nowrap">{formatDateWithTime(item.createdDate)}</td>
									</tr>
								))}
								{data.length === 0 &&
									<tr>
										<td colSpan="9" className="text-center text-muted py-4">
											{t('LIST_IS_EMPTY')}
										</td>
									</tr>
								}
							</tbody>
							{data.length > 0 &&
								<tfoot>
									<tr style={{ fontWeight: 'bold', borderTop: '2px solid #ccc' }}>
										<td colSpan="4" className="text-end">{t('TOTAL')} ({t('ON_THIS_PAGE') || t('PAGE') || 'page'}):</td>
										<td className="text-center">{totalMonths()}</td>
										<td className="text-end">{formatMoney(totalForegone())}</td>
										<td colSpan="3"></td>
									</tr>
								</tfoot>
							}
						</table>
					</div>

					<div className="d-flex justify-content-between align-items-center mt-2">
						<span className="text-muted">{t('TOTAL')}: {totalCount}</span>
						<Pagination pageCount={pageCount} paginate={paginate} />
					</div>
				</div>
			</div>
		</>
	)
}

export default Index
